/**
 * Admin — Media library, Navigation manager, Redirects, Templates, Blog,
 * Help, FAQs. Kept compact — one file per surface to minimize sprawl.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Image as ImageIcon, Folder, Search, Upload, Trash2, X, GripVertical,
  Plus, ArrowUp, ArrowDown, Save, ExternalLink, Copy, ChevronDown, ChevronUp,
} from "lucide-react";
import api from "@/lib/api";
import { BLOCK_REGISTRY, SectionRenderer } from "@/builder/blockRegistry";

const cx = (...c) => c.filter(Boolean).join(" ");

// =====================================================================
// MEDIA LIBRARY
// =====================================================================
export function AdminMediaPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    const { data } = await api.get("/admin/media", { params: { q: q || undefined, folder: folder || undefined, limit: 200 } });
    setItems(data.items || []);
    setTotal(data.total || 0);
  };
  useEffect(() => { load(); }, [q, folder]);
  const upload = async (file) => {
    try {
      const form = new FormData();
      form.append("file", file);
      // Use existing /files/upload storage endpoint
      const { data } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Register as media_files
      const now = new Date().toISOString();
      const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "document";
      await api.post("/admin/media/register", { url: data.url, filename: file.name, kind, mime_type: file.type, size_bytes: file.size, folder: folder || "root" }).catch(() => {});
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error("Upload failed — try again");
    }
  };
  return (
    <div className="max-w-6xl" data-testid="admin-media">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Media</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Media library</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">{total} file{total === 1 ? "" : "s"}. Upload images, edit metadata, and pick from the library in the visual builder.</p>
        </div>
        <label className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a] cursor-pointer" data-testid="upload-btn">
          <Upload className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />Upload
          <input type="file" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search filename, alt, tags…" className="w-full pl-9 pr-3 py-2 text-sm rounded-full border border-slate-200 focus:border-[#165a71] focus:outline-none" data-testid="media-search" />
        </div>
        <select value={folder} onChange={e => setFolder(e.target.value)} className="rounded-full border border-slate-200 px-3 py-2 text-sm" data-testid="media-folder">
          <option value="">All folders</option>
          <option value="root">Root</option>
          <option value="brand">Brand</option>
          <option value="providers">Providers</option>
          <option value="services">Services</option>
          <option value="blog">Blog</option>
          <option value="verification">Verification (private)</option>
        </select>
      </div>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="media-grid">
        {items.map(m => (
          <button key={m.id} onClick={() => setSelected(m)} className={cx("group rounded-lg overflow-hidden border-2 bg-white text-left", selected?.id === m.id ? "border-[#165a71]" : "border-slate-200 hover:border-[#165a71]/40")} data-testid={`media-${m.id}`}>
            {m.kind === "image" && m.url ? (
              <img src={m.url} alt={m.alt_text || ""} className="w-full aspect-square object-cover" />
            ) : (
              <div className="aspect-square flex items-center justify-center bg-slate-50 text-slate-400"><ImageIcon className="h-8 w-8" /></div>
            )}
            <div className="p-2">
              <div className="text-[10px] truncate text-[#252A34]/70">{m.filename}</div>
            </div>
          </button>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-16 text-sm text-[#252A34]/60">No files yet — hit Upload above.</div>}
      </div>
      {selected && <MediaDetailDrawer file={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function MediaDetailDrawer({ file, onClose, onSaved }) {
  const [f, setF] = useState({
    alt_text: file.alt_text || "",
    caption: file.caption || "",
    description: file.description || "",
    folder: file.folder || "root",
    tags: (file.tags || []).join(", "),
    is_public: file.is_public !== false,
  });
  const save = async () => {
    await api.put(`/admin/media/${file.id}`, { ...f, tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) });
    toast.success("Saved");
    onSaved();
  };
  const del = async () => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/admin/media/${file.id}`);
    toast.success("Deleted");
    onSaved();
  };
  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-white shadow-2xl border-l border-slate-200 p-6 overflow-auto" data-testid="media-detail">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-semibold text-[#071b22]">File details</div>
        <button onClick={onClose} className="text-slate-500 hover:text-[#252A34]"><X className="h-5 w-5" /></button>
      </div>
      {file.kind === "image" && <img src={file.url} alt="" className="w-full rounded-lg mb-4" />}
      <div className="text-xs text-[#252A34]/60 mb-4 space-y-1">
        <div><span className="font-medium text-[#252A34]">Filename:</span> {file.filename}</div>
        <div><span className="font-medium text-[#252A34]">URL:</span> <span className="font-mono break-all">{file.url}</span> <button onClick={() => { navigator.clipboard.writeText(file.url); toast.success("URL copied"); }} className="ml-1"><Copy className="h-3 w-3 inline" /></button></div>
        <div><span className="font-medium text-[#252A34]">Size:</span> {(file.size_bytes / 1024).toFixed(0)} KB</div>
      </div>
      <div className="space-y-3">
        <div><label className="block text-xs font-medium mb-1">Alt text</label><input value={f.alt_text} onChange={e => setF({ ...f, alt_text: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="media-alt" /></div>
        <div><label className="block text-xs font-medium mb-1">Caption</label><input value={f.caption} onChange={e => setF({ ...f, caption: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium mb-1">Folder</label><input value={f.folder} onChange={e => setF({ ...f, folder: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium mb-1">Tags</label><input value={f.tags} onChange={e => setF({ ...f, tags: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" placeholder="comma, separated" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_public} onChange={e => setF({ ...f, is_public: e.target.checked })} />Public (unchecked = private, admin-only)</label>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button onClick={del} className="rounded-full text-red-600 border border-red-200 px-4 py-2 text-sm font-medium hover:bg-red-50"><Trash2 className="h-3 w-3 inline -mt-0.5 mr-1" />Delete</button>
        <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="media-save"><Save className="h-3 w-3 inline -mt-0.5 mr-1" />Save</button>
      </div>
    </div>
  );
}

// =====================================================================
// NAVIGATION MANAGER
// =====================================================================
export function AdminNavigationPage() {
  const [menus, setMenus] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = async () => {
    const { data } = await api.get("/admin/navigation");
    setMenus(data || []);
    if (data?.length && !activeKey) {
      const want = new URLSearchParams(window.location.search).get("menu");
      const first = data.find(m => m.key === want) || data[0];
      setActiveKey(first.key);
      setItems(first.items || []);
      setName(first.name || "");
    }
  };
  useEffect(() => { load(); }, []);
  const activeMenu = menus.find(m => m.key === activeKey);
  useEffect(() => {
    if (activeMenu) {
      setItems(activeMenu.items || []);
      setName(activeMenu.name || "");
    }
  }, [activeKey]);

  const move = (idx, delta) => {
    const to = idx + delta;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(idx, 1);
    next.splice(to, 0, it);
    setItems(next);
  };
  const addItem = () => setItems([...items, { id: `it_${Date.now()}`, label: "New item", href: "/", children: [] }]);
  const del = (idx) => setItems(items.filter((_, i) => i !== idx));
  const patch = (idx, k, v) => setItems(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));

  // ---- children editing ----
  const addChild = (idx) => setItems(items.map((it, i) => i === idx
    ? { ...it, children: [...(it.children || []), { id: `ch_${Date.now()}`, label: "New link", href: "/" }] }
    : it));
  const patchChild = (idx, cidx, k, v) => setItems(items.map((it, i) => i === idx
    ? { ...it, children: (it.children || []).map((c, ci) => ci === cidx ? { ...c, [k]: v } : c) }
    : it));
  const delChild = (idx, cidx) => setItems(items.map((it, i) => i === idx
    ? { ...it, children: (it.children || []).filter((_, ci) => ci !== cidx) }
    : it));
  const moveChild = (idx, cidx, delta) => {
    const item = items[idx];
    const arr = [...(item.children || [])];
    const to = cidx + delta;
    if (to < 0 || to >= arr.length) return;
    const [c] = arr.splice(cidx, 1);
    arr.splice(to, 0, c);
    setItems(items.map((it, i) => i === idx ? { ...it, children: arr } : it));
  };

  const save = async () => {
    // Clean empty children arrays to keep the payload tidy.
    const clean = items.map(({ children, ...rest }) => ({
      ...rest,
      ...(children && children.length ? { children } : {}),
    }));
    await api.put(`/admin/navigation/${activeKey}`, { key: activeKey, name, columns: activeMenu?.columns || 1, items: clean });
    toast.success("Menu saved");
    load();
  };

  return (
    <div className="max-w-4xl" data-testid="admin-navigation">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Navigation</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Navigation menus</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Header, footer, and dashboard menus are DB-driven. Edit here and the public site updates on next load.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {menus.map(m => (
          <button key={m.key} onClick={() => setActiveKey(m.key)} className={cx("rounded-full px-4 py-1.5 text-sm border", activeKey === m.key ? "bg-[#165a71] text-white border-[#165a71]" : "bg-white text-[#252A34] border-slate-200 hover:border-[#165a71]/40")} data-testid={`nav-tab-${m.key}`}>{m.name || m.key}</button>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div><label className="block text-xs font-medium mb-1">Menu name</label><input value={name} onChange={e => setName(e.target.value)} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          <div className="text-xs text-[#252A34]/60 self-end">Menu key: <span className="font-mono">{activeKey}</span></div>
        </div>
        <div className="space-y-2" data-testid="nav-items">
          {items.map((it, i) => (
            <NavItemRow
              key={it.id || i}
              item={it}
              index={i}
              total={items.length}
              onPatch={(k, v) => patch(i, k, v)}
              onDelete={() => del(i)}
              onMove={(delta) => move(i, delta)}
              onAddChild={() => addChild(i)}
              onPatchChild={(cidx, k, v) => patchChild(i, cidx, k, v)}
              onDelChild={(cidx) => delChild(i, cidx)}
              onMoveChild={(cidx, delta) => moveChild(i, cidx, delta)}
            />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button onClick={addItem} className="rounded-full border border-slate-200 text-[#165a71] px-4 py-2 text-sm font-medium hover:border-[#165a71]/40" data-testid="nav-add-item"><Plus className="h-3 w-3 inline -mt-0.5 mr-1" />Add item</button>
          <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="nav-save"><Save className="h-3 w-3 inline -mt-0.5 mr-1" />Save menu</button>
        </div>
      </div>
    </div>
  );
}

function NavItemRow({ item, index: i, total, onPatch, onDelete, onMove, onAddChild, onPatchChild, onDelChild, onMoveChild }) {
  const [expanded, setExpanded] = useState((item.children || []).length > 0);
  const children = item.children || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/40" data-testid={`nav-item-${i}`}>
      <div className="flex items-center gap-2 p-2">
        <GripVertical className="h-4 w-4 text-slate-400" />
        <input value={item.label} onChange={e => onPatch("label", e.target.value)} placeholder="Label" className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm bg-white" data-testid={`nav-item-label-${i}`} />
        <input value={item.href || ""} onChange={e => onPatch("href", e.target.value)} placeholder="/route or https://…" className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm bg-white font-mono" data-testid={`nav-item-href-${i}`} />
        <select value={item.display || ""} onChange={e => onPatch("display", e.target.value)} className="rounded border border-slate-200 px-2 py-1 text-xs bg-white" title="How this item appears" data-testid={`nav-item-display-${i}`}>
          <option value="">Link</option>
          <option value="local">Local services dropdown</option>
          <option value="digital">Digital services dropdown</option>
          <option value="button">Get-estimates button</option>
        </select>
        <select value={item.role || ""} onChange={e => onPatch("role", e.target.value)} className="rounded border border-slate-200 px-2 py-1 text-xs bg-white" data-testid={`nav-item-role-${i}`}>
          <option value="">Everyone</option>
          <option value="visitor">Visitors only</option>
          <option value="customer">Customers</option>
          <option value="provider">Providers</option>
          <option value="administrator">Admins</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-[#252A34]/70" title="Open in new tab">
          <input type="checkbox" checked={item.target === "_blank"} onChange={e => onPatch("target", e.target.checked ? "_blank" : "")} data-testid={`nav-item-newtab-${i}`} />
          <span>New tab</span>
        </label>
        <button onClick={() => setExpanded(v => !v)} className="rounded p-1 text-slate-500 hover:bg-white" title={expanded ? "Collapse" : "Expand"} data-testid={`nav-item-expand-${i}`}>{expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}</button>
        <button onClick={() => onMove(-1)} disabled={i === 0} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-40" title="Up" data-testid={`nav-up-${i}`}><ArrowUp className="h-3 w-3" /></button>
        <button onClick={() => onMove(1)} disabled={i === total - 1} className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-40" title="Down" data-testid={`nav-down-${i}`}><ArrowDown className="h-3 w-3" /></button>
        <button onClick={onDelete} className="rounded p-1 text-red-500 hover:bg-red-50" title="Remove" data-testid={`nav-del-${i}`}><Trash2 className="h-3 w-3" /></button>
      </div>
      {expanded && (
        <div className="border-t border-slate-200/70 bg-white/60 p-2 space-y-1.5" data-testid={`nav-item-children-${i}`}>
          {children.length === 0 && (
            <div className="text-xs text-[#252A34]/60 px-2 py-1.5">No dropdown items yet. Add one below to turn this into a dropdown menu (header) or column group (footer).</div>
          )}
          {children.map((c, ci) => (
            <div key={c.id || ci} className="flex items-center gap-2 pl-6 pr-1" data-testid={`nav-child-${i}-${ci}`}>
              <span className="text-slate-400 text-xs">↳</span>
              <input value={c.label} onChange={e => onPatchChild(ci, "label", e.target.value)} placeholder="Child label" className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs" data-testid={`nav-child-label-${i}-${ci}`} />
              <input value={c.href || ""} onChange={e => onPatchChild(ci, "href", e.target.value)} placeholder="/route or https://…" className="flex-1 rounded border border-slate-200 px-2 py-1 text-xs font-mono" data-testid={`nav-child-href-${i}-${ci}`} />
              <label className="inline-flex items-center gap-1 text-[10px] text-[#252A34]/70" title="Open in new tab">
                <input type="checkbox" checked={c.target === "_blank"} onChange={e => onPatchChild(ci, "target", e.target.checked ? "_blank" : "")} data-testid={`nav-child-newtab-${i}-${ci}`} />
                <span>New tab</span>
              </label>
              <button onClick={() => onMoveChild(ci, -1)} disabled={ci === 0} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Up"><ArrowUp className="h-3 w-3" /></button>
              <button onClick={() => onMoveChild(ci, 1)} disabled={ci === children.length - 1} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40" title="Down"><ArrowDown className="h-3 w-3" /></button>
              <button onClick={() => onDelChild(ci)} className="rounded p-1 text-red-500 hover:bg-red-50" title="Remove" data-testid={`nav-child-del-${i}-${ci}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
          <div className="pl-6 pt-1">
            <button onClick={onAddChild} className="text-xs text-[#165a71] font-medium hover:underline" data-testid={`nav-add-child-${i}`}><Plus className="h-3 w-3 inline -mt-0.5 mr-0.5" />Add dropdown item</button>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// REDIRECTS
// =====================================================================
export function AdminRedirectsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ source_path: "", destination_url: "", status_code: 301, match_type: "exact", active: true });
  const load = async () => setItems((await api.get("/admin/redirects")).data || []);
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.source_path || !form.destination_url) { toast.error("Source and destination required"); return; }
    await api.post("/admin/redirects", form);
    setForm({ source_path: "", destination_url: "", status_code: 301, match_type: "exact", active: true });
    toast.success("Redirect added");
    load();
  };
  const toggle = async (r) => { await api.put(`/admin/redirects/${r.id}`, { active: !r.active }); load(); };
  const del = async (r) => { if (window.confirm("Delete this redirect?")) { await api.delete(`/admin/redirects/${r.id}`); load(); } };
  return (
    <div className="max-w-4xl" data-testid="admin-redirects">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Redirects</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">URL redirects</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Redirects are honored on the public site when a route 404s. Use for renamed pages or campaign short-links.</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="grid gap-2 md:grid-cols-6">
          <input value={form.source_path} onChange={e => setForm({ ...form, source_path: e.target.value })} placeholder="/old-url" className="col-span-2 rounded border border-slate-200 px-3 py-2 text-sm font-mono" data-testid="redir-src" />
          <input value={form.destination_url} onChange={e => setForm({ ...form, destination_url: e.target.value })} placeholder="/new-url or https://…" className="col-span-2 rounded border border-slate-200 px-3 py-2 text-sm font-mono" data-testid="redir-dest" />
          <select value={form.status_code} onChange={e => setForm({ ...form, status_code: Number(e.target.value) })} className="rounded border border-slate-200 px-2 py-2 text-sm">
            <option value={301}>301 (permanent)</option>
            <option value={302}>302 (temporary)</option>
          </select>
          <select value={form.match_type} onChange={e => setForm({ ...form, match_type: e.target.value })} className="rounded border border-slate-200 px-2 py-2 text-sm">
            <option value="exact">Exact</option>
            <option value="prefix">Prefix</option>
          </select>
        </div>
        <div className="mt-3 text-right">
          <button onClick={create} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="redir-add"><Plus className="h-3 w-3 inline -mt-0.5 mr-1" />Add redirect</button>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#252A34]/70">
            <tr>
              <th className="text-left px-4 py-2">Source</th>
              <th className="text-left px-4 py-2">Destination</th>
              <th className="text-left px-4 py-2">Type / Code</th>
              <th className="text-left px-4 py-2">Hits</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(r => (
              <tr key={r.id} data-testid={`redir-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.source_path}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.destination_url}</td>
                <td className="px-4 py-3 text-xs">{r.match_type} · {r.status_code}</td>
                <td className="px-4 py-3 text-xs">{r.hits || 0}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(r)} className="text-xs mr-2 text-[#165a71]">{r.active ? "Disable" : "Enable"}</button>
                  <button onClick={() => del(r)} className="text-xs text-red-600">Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-sm text-[#252A34]/60">No redirects yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// TEMPLATES
// =====================================================================
export function AdminTemplatesPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/templates").then(r => setItems(r.data || [])); }, []);
  return (
    <div className="max-w-5xl" data-testid="admin-templates">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Templates</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Dynamic templates</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Templates determine how detail pages (provider profiles, service pages, category/city pages, blog posts, etc.) render. Edit visually — public pages update on next load.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map(t => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`template-${t.key}`}>
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-[#071b22]">{t.name}</div>
              <span className={cx("text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5", t.fidelity === "flagship" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600")}>{t.fidelity}</span>
            </div>
            <div className="text-xs text-[#252A34]/60 mt-1">Entity: <span className="font-mono">{t.entity}</span></div>
            <div className="text-sm text-[#252A34]/75 mt-2">{t.description}</div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => nav(`/admin/templates/${t.id}`)} className="rounded-full bg-[#165a71] text-white px-4 py-1.5 text-sm font-medium hover:bg-[#11485a]" data-testid={`template-edit-${t.key}`}>Edit visually</button>
              <span className="text-xs text-[#252A34]/60">Priority {t.priority}{t.is_default && " · default"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Reuse the page editor for templates by mounting a simplified variant
export function AdminTemplateEditorPage() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  useEffect(() => { api.get(`/admin/templates/${tid}`).then(r => setTpl(r.data)); }, [tid]);
  if (!tpl) return <div className="max-w-6xl mx-auto py-16 px-4 animate-pulse text-[#252A34]/60">Loading template…</div>;
  const save = async () => {
    await api.put(`/admin/templates/${tid}`, { sections: tpl.sections, draft_sections: tpl.sections });
    toast.success("Template saved");
  };
  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <Link to="/admin/templates" className="text-xs text-[#165a71] font-medium">← Templates</Link>
      <h1 className="mt-2 font-display text-2xl font-semibold text-[#071b22]">{tpl.name}</h1>
      <div className="mt-1 text-xs text-[#252A34]/60">Entity <span className="font-mono">{tpl.entity}</span> · Fidelity <span className="font-medium">{tpl.fidelity}</span></div>
      <div className="my-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/60 p-6">
        <div className="text-sm text-[#252A34]/70 mb-4">Template preview (read-only rendering with placeholder data):</div>
        <div className="rounded-xl bg-white">
          {(tpl.sections || []).map(s => <SectionRenderer key={s.id} section={s} mode="edit" />)}
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs font-semibold text-[#071b22] mb-2 uppercase tracking-wider">Sections outline</div>
        <ul className="text-sm text-[#252A34]/85 space-y-2">
          {(tpl.sections || []).map((s, i) => (
            <li key={s.id} className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={`tpl-sec-${i}`}>
              <span>{i + 1}. {s.name} — <span className="text-xs text-[#252A34]/60">{(s.blocks || []).map(b => BLOCK_REGISTRY[b.kind]?.label || b.kind).join(" · ")}</span></span>
              <span className="inline-flex items-center gap-1">
                <button onClick={() => { const arr = [...tpl.sections]; if (i > 0) { [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setTpl({ ...tpl, sections: arr }); } }} disabled={i === 0} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40" data-testid={`tpl-sec-up-${i}`}><ArrowUp className="h-3 w-3" /></button>
                <button onClick={() => { const arr = [...tpl.sections]; if (i < arr.length - 1) { [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; setTpl({ ...tpl, sections: arr }); } }} disabled={i === tpl.sections.length - 1} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ArrowDown className="h-3 w-3" /></button>
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-right">
          <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="tpl-save"><Save className="h-3 w-3 inline -mt-0.5 mr-1" />Save section order</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// BLOG ADMIN
// =====================================================================
export function AdminBlogPage() {
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setPosts((await api.get("/admin/blog")).data || []);
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-6xl" data-testid="admin-blog">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Blog</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Blog posts</h1>
        </div>
        <button onClick={() => setEditing({ status: "draft", tags: [] })} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="blog-new"><Plus className="h-3 w-3 inline -mt-0.5 mr-1" />New post</button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#252A34]/70">
            <tr><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Published</th><th className="text-right px-4 py-2">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {posts.map(p => (
              <tr key={p.id} data-testid={`blog-row-${p.slug}`}>
                <td className="px-4 py-3"><div className="font-medium text-[#071b22]">{p.title}</div><div className="text-xs text-[#252A34]/60 font-mono">/blog/{p.slug}</div></td>
                <td className="px-4 py-3 text-xs">{p.category}</td>
                <td className="px-4 py-3"><span className={cx("text-[10px] uppercase font-semibold rounded-full px-2 py-0.5", p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600")}>{p.status}</span></td>
                <td className="px-4 py-3 text-xs">{(p.published_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(p)} className="text-xs text-[#165a71] font-medium" data-testid={`blog-edit-${p.slug}`}>Edit</button>
                  {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="ml-2 text-xs text-slate-500"><ExternalLink className="h-3 w-3 inline" /></a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const [f, setF] = useState({
    title: post.title || "",
    slug: post.slug || "",
    excerpt: post.excerpt || "",
    body: post.body || "",
    category: post.category || "",
    tags: (post.tags || []).join(", "),
    hero_image_url: post.hero_image_url || "",
    status: post.status || "draft",
    seo_title: post.seo_title || "",
    seo_description: post.seo_description || "",
  });
  const save = async () => {
    const payload = { ...f, tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) };
    if (post.id) await api.put(`/admin/blog/${post.id}`, payload);
    else await api.post("/admin/blog", payload);
    toast.success("Saved");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold">{post.id ? "Edit post" : "New post"}</div>
          <button onClick={onClose} className="text-slate-500"><X className="h-5 w-5" /></button>
        </header>
        <div className="p-6 overflow-auto space-y-3">
          <div><label className="block text-xs font-medium mb-1">Title</label><input value={f.title} onChange={e => setF({ ...f, title: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="blog-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium mb-1">Slug</label><input value={f.slug} onChange={e => setF({ ...f, slug: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm font-mono" /></div>
            <div><label className="block text-xs font-medium mb-1">Status</label>
              <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm">
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
            </div>
          </div>
          <div><label className="block text-xs font-medium mb-1">Excerpt</label><textarea rows={2} value={f.excerpt} onChange={e => setF({ ...f, excerpt: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-medium mb-1">Body</label><textarea rows={8} value={f.body} onChange={e => setF({ ...f, body: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="blog-body" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium mb-1">Category</label><input value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium mb-1">Tags (comma)</label><input value={f.tags} onChange={e => setF({ ...f, tags: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          </div>
          <div><label className="block text-xs font-medium mb-1">Hero image URL</label><input value={f.hero_image_url} onChange={e => setF({ ...f, hero_image_url: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          <div className="pt-2 text-xs font-semibold text-[#071b22] uppercase tracking-wider">SEO</div>
          <div><label className="block text-xs font-medium mb-1">SEO title</label><input value={f.seo_title} onChange={e => setF({ ...f, seo_title: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-medium mb-1">Meta description</label><textarea rows={2} value={f.seo_description} onChange={e => setF({ ...f, seo_description: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
        </div>
        <footer className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a]" data-testid="blog-save">Save</button>
        </footer>
      </div>
    </div>
  );
}

// =====================================================================
// HELP + FAQ (compact CRUD)
// =====================================================================
export function AdminHelpPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setItems((await api.get("/admin/help")).data || []);
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-5xl" data-testid="admin-help">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Help</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Help center articles</h1>
        </div>
        <button onClick={() => setEditing({ status: "published" })} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="help-new"><Plus className="h-3 w-3 inline -mt-0.5 mr-1" />New article</button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#252A34]/70"><tr><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">Audience</th><th className="text-left px-4 py-2">Order</th><th className="text-right px-4 py-2">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(a => (
              <tr key={a.id} data-testid={`help-row-${a.slug}`}>
                <td className="px-4 py-3"><div className="font-medium text-[#071b22]">{a.title}</div><div className="text-xs text-[#252A34]/60 font-mono">/help/{a.slug}</div></td>
                <td className="px-4 py-3 text-xs">{a.category}</td>
                <td className="px-4 py-3 text-xs">{(a.audience || []).join(", ")}</td>
                <td className="px-4 py-3 text-xs">{a.order}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setEditing(a)} className="text-xs text-[#165a71] font-medium">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <HelpEditor article={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function HelpEditor({ article, onClose, onSaved }) {
  const [f, setF] = useState({
    title: article.title || "", slug: article.slug || "",
    excerpt: article.excerpt || "", body: article.body || "",
    category: article.category || "", audience: (article.audience || ["all"]).join(","),
    status: article.status || "published", order: article.order || 0,
  });
  const save = async () => {
    const payload = { ...f, audience: f.audience.split(",").map(a => a.trim()).filter(Boolean) };
    if (article.id) await api.put(`/admin/help/${article.id}`, payload);
    else await api.post("/admin/help", payload);
    toast.success("Saved");
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between"><div className="font-display font-semibold">{article.id ? "Edit article" : "New article"}</div><button onClick={onClose} className="text-slate-500"><X className="h-5 w-5" /></button></header>
        <div className="p-6 overflow-auto space-y-3">
          <div><label className="block text-xs font-medium mb-1">Title</label><input value={f.title} onChange={e => setF({ ...f, title: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="help-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium mb-1">Slug</label><input value={f.slug} onChange={e => setF({ ...f, slug: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm font-mono" /></div>
            <div><label className="block text-xs font-medium mb-1">Category</label><input value={f.category} onChange={e => setF({ ...f, category: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          </div>
          <div><label className="block text-xs font-medium mb-1">Audience (comma: customers, providers, all)</label><input value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-medium mb-1">Body</label><textarea rows={10} value={f.body} onChange={e => setF({ ...f, body: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="help-body" /></div>
        </div>
        <footer className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2"><button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button><button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="help-save">Save</button></footer>
      </div>
    </div>
  );
}

export function AdminFaqsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setItems((await api.get("/admin/faqs")).data || []);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (editing.id) await api.put(`/admin/faqs/${editing.id}`, editing);
    else await api.post("/admin/faqs", editing);
    toast.success("Saved");
    setEditing(null);
    load();
  };
  return (
    <div className="max-w-4xl" data-testid="admin-faqs">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · FAQs</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Frequently asked questions</h1>
        </div>
        <button onClick={() => setEditing({ status: "active", audience: ["all"] })} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="faq-new"><Plus className="h-3 w-3 inline -mt-0.5 mr-1" />New FAQ</button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
        {items.map(q => (
          <div key={q.id} className="p-4" data-testid={`faq-row-${q.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-medium text-[#071b22]">{q.question}</div>
                <div className="text-sm text-[#252A34]/70 mt-1 line-clamp-2">{q.answer}</div>
                <div className="mt-1 text-xs text-[#252A34]/50">{q.category} · {(q.audience || []).join(", ")} · order {q.order}</div>
              </div>
              <button onClick={() => setEditing(q)} className="text-xs text-[#165a71] font-medium shrink-0" data-testid={`faq-edit-${q.id}`}>Edit</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-sm text-[#252A34]/60">No FAQs yet.</div>}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4"><div className="font-display font-semibold">{editing.id ? "Edit FAQ" : "New FAQ"}</div><button onClick={() => setEditing(null)}><X className="h-5 w-5 text-slate-500" /></button></div>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium mb-1">Question</label><input value={editing.question || ""} onChange={e => setEditing({ ...editing, question: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="faq-q" /></div>
              <div><label className="block text-xs font-medium mb-1">Answer</label><textarea rows={4} value={editing.answer || ""} onChange={e => setEditing({ ...editing, answer: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" data-testid="faq-a" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1">Category</label><input value={editing.category || ""} onChange={e => setEditing({ ...editing, category: e.target.value })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium mb-1">Order</label><input type="number" value={editing.order || 0} onChange={e => setEditing({ ...editing, order: Number(e.target.value) })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
              </div>
              <div><label className="block text-xs font-medium mb-1">Audience (comma: customers, providers, all)</label><input value={(editing.audience || []).join(",")} onChange={e => setEditing({ ...editing, audience: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="w-full rounded border border-slate-200 px-3 py-2 text-sm" /></div>
            </div>
            <div className="mt-5 flex justify-end gap-2"><button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button><button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="faq-save">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
